import {
    ERROR_CODE,
    getGameConfig,
    getServerTimestamp,
    getUserData,
    isConnected,
    loginWithGameBackendCredentials,
    PlainObject,
    registerWithFacebookInstantGameSignature,
    setUserData,
    setUserProfile,
    syncFriendListByPlatformPlayerId,
    trackBotMessageConversion,
} from '@sgorg/game-backend-client';

import * as Sentry from '@sentry/browser';
import getLogger, { ILogger } from '../Core/Logger';
import { waitForMs } from '../Utils/AsyncUtils';

const sgLogger: ILogger = getLogger('[SG]:');

interface ISGUserProfile {
    handle: string;
    locale: string;
}

interface IGameConfig {
    XPromo: IXPromoConfig[];
}

export interface IXPromoConfig {
    enabled: boolean;
    gameName: string;
    gameId: number;
    assets: { [key: string]: PlainObject };
}

export class SDKsg {
    public initialized: boolean = false;

    constructor() {}

    public async initGameBackend(fbPromise: any, run = 1): Promise<any> {
        const gameId = `moji${SUFFIX ? `-${SUFFIX}` : ''}`;
        const authDataKey = 'authData';

        try {
            const { userId, authToken } = await this.getCredentials();
            await loginWithGameBackendCredentials({
                gameId,
                userId,
                authToken,
            });

            const data = await fbPromise;
            const { name, locale, friends } = data;
            this.initialized = true;
            this.syncUserData({ handle: name, locale }, friends).catch(err => {
                Sentry.captureException(err);
                Sentry.captureMessage('Error with syncUserData');
            });
        } catch (error) {
            if (run > 7) {
                this.initialized = false;
                Sentry.captureMessage(`Cannot init gbc. Moving on. 
                Error: ${JSON.stringify(error)}`);
            } else {
                if (error.code === ERROR_CODE.CONNECTION_NOT_INITIALIZED) {
                    await waitForMs(4000);
                } else if (
                    error.code === ERROR_CODE.NOT_AUTHORIZED ||
                    error.code === ERROR_CODE.NOT_FOUND
                ) {
                    await FBInstant.player.setDataAsync({ [authDataKey]: null });
                }
                await waitForMs(1000);
                return this.initGameBackend(fbPromise, run + 1);
            }
        }
    }

    public async syncUserData(userInfo: ISGUserProfile, friends: any[]) {
        await setUserProfile(userInfo);
        await syncFriendListByPlatformPlayerId({
            friendList: friends.map(f => f.userId),
        });
    }

    // these will be to retrieve/ store to gamebackend!!
    public async retrieveData(key: string): Promise<any> {
        if (await this.checkConnection()) {
            const userData: any = await getUserData();
            if (userData[key]) {
                return userData[key];
            }
        }
        return null;
    }

    public async storeData(key: string, value: any) {
        if (!(await this.checkConnection())) {
            return;
        }
        const obj = {
            [key]: value,
        };
        await setUserData(obj);
    }

    public isInitialized() {
        return this.initialized;
    }

    public async checkConnection() {
        return isConnected();
    }

    public async fetchServerTimestamp() {
        return await getServerTimestamp();
    }

    public async getConfig(): Promise<IGameConfig> {
        try {
            if (await this.checkConnection()) {
                const config = await getGameConfig();
                return (config as unknown) as IGameConfig;
            }
        } catch (e) {
            Sentry.captureException(e);
        }
        return { XPromo: [] };
    }

    public async trackBotConversion() {
        const entryPointData = FBInstant.getEntryPointData();
        if (entryPointData && entryPointData.messageId) {
            try {
                await trackBotMessageConversion({ messageId: entryPointData.messageId });
            } catch (error) {
                sgLogger.debug(['Error message', error.message]);
                Sentry.captureMessage(
                    `Error with trackBotConversionError: ${JSON.stringify(error)}`
                );
            }
        }
    }

    private async getCredentials(): Promise<{ userId: string; authToken: string }> {
        let gameId = 'moji';
        if (SUFFIX) {
            gameId = gameId + '-' + SUFFIX;
        }
        const authDataKey = 'authData';

        const fb = window.FBInstant;
        const data = await FBInstant.player.getDataAsync([authDataKey]);

        const authData = data && data[authDataKey] ? data[authDataKey] : {};
        const { userId: oldUserId, authToken: oldAuthToken } = authData;
        if (oldUserId && oldAuthToken) {
            return { userId: oldUserId, authToken: oldAuthToken };
        }

        const playerInfo = await fb.player.getSignedPlayerInfoAsync();
        const signature = playerInfo.getSignature();
        const { userId, authToken } = await registerWithFacebookInstantGameSignature({
            gameId,
            signature,
        });
        if (typeof userId !== 'string' || !userId.trim()) {
            throw new Error(`Received invalid userId from GBC registration`);
        }
        if (typeof authToken !== 'string' || !authToken.trim()) {
            throw new Error(`Received invalid authToken from GBC registration`);
        }
        await FBInstant.player.setDataAsync({ [authDataKey]: { userId, authToken } });
        return { userId, authToken };
    }
}
